import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, User, Loader2, Eye, EyeOff, Tag, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PUBLIC_ROLES = [
  { id: 'athlete', name: 'Athlete / Player', description: 'Access your games, stats, and build your profile' },
  { id: 'coach', name: 'Coach', description: 'Manage teams, analyze performance, and scout talent' },
  { id: 'parent', name: 'Parent / Family', description: "Watch and share your athlete's moments" },
  { id: 'scout', name: 'Scout / Recruiter', description: 'Discover and recruit talented athletes' },
];

const Register = () => {
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: '', discountCode: '' });
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [discountValid, setDiscountValid] = useState(null); // null=not checked, true/false
  const [discountInfo, setDiscountInfo] = useState(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const signupSource = searchParams.get('from') || null;  // e.g. "event:abc-123"
  const returnTo = searchParams.get('return_to');  // e.g. "/event/abc-123"

  const handleRoleSelect = (roleId) => {
    setFormData({ ...formData, role: roleId });
    setStep(2);
  };

  const validateDiscount = async () => {
    if (!formData.discountCode.trim()) return;
    setCheckingCode(true);
    try {
      const res = await fetch(`${API_URL}/api/discount-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: formData.discountCode }),
      });
      const data = await res.json();
      setDiscountValid(data.valid);
      setDiscountInfo(data.valid ? data : null);
    } catch {
      setDiscountValid(false);
    }
    setCheckingCode(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await register(
      formData.name,
      formData.email,
      formData.password,
      formData.role,
      discountValid ? formData.discountCode : null,
      signupSource
    );
    if (result.success) {
      const needsPayment = result.user?.subscription_status === 'pending_payment';
      if (returnTo) {
        // Redirect back to source event/page if provided (works for free-with-account events)
        toast.success('Account Created!', { description: 'Welcome to Rooam Sports!' });
        navigate(returnTo);
      } else if (needsPayment) {
        toast.success('Account Created!', { description: 'Complete your membership to get started.' });
        navigate('/membership?signup=true');
      } else {
        toast.success('Account Created!', { description: 'Welcome to Rooam Sports!' });
        navigate('/events');
      }
    } else {
      toast.error('Registration Failed', { description: result.error });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-rooam-black flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-xl">
        <Link to="/" className="flex items-center justify-center mb-8">
          <img src="/rooam-logo.png" alt="Rooam Sports Network" className="h-44 w-auto" />
        </Link>

        <Card className="bg-rooam-gray-medium border-rooam-gray-light shadow-2xl">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-white">
              {step === 1 ? 'Join Rooam Sports' : 'Create Your Account'}
            </CardTitle>
            <p className="text-center text-gray-400 text-sm">
              {step === 1 ? 'Select the role that best describes you' : 'Fill in your details to get started'}
            </p>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {PUBLIC_ROLES.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => handleRoleSelect(role.id)}
                    data-testid={`role-select-${role.id}`}
                    className="text-left p-4 border-2 border-rooam-gray-light bg-rooam-black rounded-lg hover:border-rooam-blue hover:bg-rooam-gray-dark transition-all group"
                  >
                    <h3 className="font-semibold text-white mb-1 group-hover:text-rooam-blue">{role.name}</h3>
                    <p className="text-xs text-gray-400">{role.description}</p>
                  </button>
                ))}
              </div>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-gray-300">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <Input
                      id="name" type="text" placeholder="John Doe"
                      className="pl-10 bg-rooam-black border-rooam-gray-light text-white placeholder-gray-500"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="register-name-input" required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-300">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <Input
                      id="email" type="email" placeholder="your@email.com"
                      className="pl-10 bg-rooam-black border-rooam-gray-light text-white placeholder-gray-500"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="register-email-input" required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password" className="text-gray-300">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <Input
                      id="password" type={showPassword ? "text" : "password"} placeholder="Create a password"
                      className="pl-10 pr-10 bg-rooam-black border-rooam-gray-light text-white placeholder-gray-500"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      data-testid="register-password-input" required minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Discount Code */}
                <div>
                  <Label htmlFor="discount" className="text-gray-300">Discount Code <span className="text-zinc-500">(optional)</span></Label>
                  <div className="flex gap-2 mt-1">
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                      <Input
                        id="discount" type="text" placeholder="Enter code"
                        className="pl-9 bg-rooam-black border-rooam-gray-light text-white placeholder-gray-500 uppercase"
                        value={formData.discountCode}
                        onChange={(e) => {
                          setFormData({ ...formData, discountCode: e.target.value });
                          setDiscountValid(null);
                          setDiscountInfo(null);
                        }}
                        data-testid="register-discount-input"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={validateDiscount}
                      disabled={!formData.discountCode.trim() || checkingCode}
                      className="border-rooam-gray-light text-gray-300 hover:bg-rooam-gray-light shrink-0"
                      data-testid="validate-discount-btn"
                    >
                      {checkingCode ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                  {discountValid === true && (
                    <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1" data-testid="discount-valid">
                      <Check className="h-3 w-3" /> {discountInfo?.discount_percent}% discount applied
                    </p>
                  )}
                  {discountValid === false && (
                    <p className="text-xs text-red-400 mt-1 flex items-center gap-1" data-testid="discount-invalid">
                      <X className="h-3 w-3" /> Invalid or expired code
                    </p>
                  )}
                </div>

                <div className="p-3 rounded-lg bg-rooam-blue/10 border border-rooam-blue">
                  <p className="text-sm text-gray-300">
                    <span className="font-semibold text-rooam-blue">Role:</span>{' '}
                    {PUBLIC_ROLES.find((r) => r.id === formData.role)?.name}
                  </p>
                  {!(discountValid && discountInfo?.discount_percent >= 100) && (
                    <p className="text-xs text-zinc-500 mt-1">A membership plan is required after signup to access the platform.</p>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button" variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 border-rooam-gray-light text-gray-300 hover:bg-rooam-gray-light"
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold"
                    disabled={loading}
                    data-testid="register-submit-button"
                  >
                    {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : 'Create Account'}
                  </Button>
                </div>
              </form>
            )}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="text-rooam-blue hover:text-rooam-blue-light font-medium">Sign in</Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
